import React, { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    IDraggingType,
    IMarquee,
    IPoint,
    IUniversalItem,
} from "userful-chronos-app-common-js/dist/itemsControl/itemsControl";
import {
    useItemsControlDispatch,
    useItemsControlSelector,
    ItemsControlActions,
} from "userful-chronos-common-store/dist/itemsControl";
import "./ClickAndDragZone.scss";

const MARQUEE_DRAG_THRESHOLD = 5; // PIXELS

interface IProps {
    id: string;
    debug?: boolean;
    children?: React.ReactNode;
}

type ItemRef = React.RefObject<HTMLElement | null> | React.MutableRefObject<HTMLElement | null>;

interface IRegisteredItem {
    item: IUniversalItem;
    ref: ItemRef;
}

interface IClickAndDragZoneContextValue {
    registerItem: (item: IUniversalItem, ref: ItemRef) => void;
    unregisterItem: (id: string) => void;
}

export const ClickAndDragZoneContext = React.createContext<IClickAndDragZoneContextValue | null>(null);

function normalizeRect(start: IPoint, current: IPoint) {
    const left = Math.min(start.x, current.x);
    const right = Math.max(start.x, current.x);
    const top = Math.min(start.y, current.y);
    const bottom = Math.max(start.y, current.y);

    return { left, right, top, bottom, width: right - left, height: bottom - top };
}

function clampPoint(point: IPoint, rect: DOMRect | null): IPoint {
    if (!rect) {
        return point;
    }
    return {
        x: Math.min(rect.right, Math.max(rect.left, point.x)),
        y: Math.min(rect.bottom, Math.max(rect.top, point.y)),
    };
}

function rectanglesIntersect(selection: { left: number; right: number; top: number; bottom: number }, rect: DOMRect) {
    return (
        selection.left <= rect.right &&
        selection.right >= rect.left &&
        selection.top <= rect.bottom &&
        selection.bottom >= rect.top
    );
}

function mergeUniqueById(primary: IUniversalItem[], secondary: IUniversalItem[]) {
    const map = new Map<string, IUniversalItem>();
    primary.forEach((item) => map.set(item.id, item));
    secondary.forEach((item) => map.set(item.id, item));
    return Array.from(map.values());
}
export default function ClickAndDragZone(props: IProps) {
    const dispatch = useItemsControlDispatch();

    const zoneRef = useRef<HTMLDivElement | null>(null);
    const pointerIdRef = useRef<number | null>(null);
    const startPointRef = useRef<IPoint | null>(null);
    const marqueeActiveRef = useRef(false);
    const registeredItemsRef = useRef<Map<string, IRegisteredItem>>(new Map());
    const [registryVersion, setRegistryVersion] = useState(0);
    const selectedAtDragStartRef = useRef<IUniversalItem[]>([]);
    const preselectedRef = useRef<IUniversalItem[]>([]);
    const overDropzoneRef = useRef<IUniversalItem | null>(null);

    const marquee: IMarquee | null = useItemsControlSelector((state) => state.itemsControl.marquee);
    const draggingType: IDraggingType = useItemsControlSelector((state) => state.itemsControl.draggingType);
    const preselectedItems: IUniversalItem[] = useItemsControlSelector((state) => state.itemsControl.preselectedItems);
    const selectedItems: IUniversalItem[] = useItemsControlSelector((state) => state.itemsControl.selectedItems);
    const draggedItems: IUniversalItem[] = useItemsControlSelector((state) => state.itemsControl.draggedItems);
    const overDropzone: IUniversalItem | null = useItemsControlSelector((state) => state.itemsControl.overDropzone);

    useEffect(() => {
        preselectedRef.current = preselectedItems;
    }, [preselectedItems]);

    useEffect(() => {
        overDropzoneRef.current = overDropzone;
    }, [overDropzone]);

    const registerItem = useCallback((item: IUniversalItem, ref: ItemRef) => {
        registeredItemsRef.current.set(item.id, { item, ref });
        setRegistryVersion((value) => value + 1);
    }, []);

    const unregisterItem = useCallback(
        (id: string) => {
            const removed = registeredItemsRef.current.delete(id);
            if (removed) {
                if (overDropzoneRef.current?.id === id) {
                    dispatch(ItemsControlActions.updateOverDropzone(null));
                }
                setRegistryVersion((value) => value + 1);
            }
        },
        [dispatch],
    );

    const resetMarquee = useCallback(() => {
        pointerIdRef.current = null;
        startPointRef.current = null;
        marqueeActiveRef.current = false;
        dispatch(ItemsControlActions.updateMarquee(null));
    }, [dispatch]);

    const handlePointerMove = useCallback(
        (event: PointerEvent) => {
            if (pointerIdRef.current !== null && event.pointerId !== pointerIdRef.current) {
                return;
            }
            const start = startPointRef.current;
            if (!start) {
                return;
            }

            const rect = zoneRef.current?.getBoundingClientRect() ?? null;
            const current = clampPoint({ x: event.clientX, y: event.clientY }, rect);

            const deltaX = Math.abs(current.x - start.x);
            const deltaY = Math.abs(current.y - start.y);
            const movedEnough = deltaX >= MARQUEE_DRAG_THRESHOLD || deltaY >= MARQUEE_DRAG_THRESHOLD;

            if (!marqueeActiveRef.current && !movedEnough) {
                return;
            }

            marqueeActiveRef.current = true;
            dispatch(ItemsControlActions.updateMarquee({ start, current }));
        },
        [dispatch],
    );

    const handlePointerUp = useCallback(
        (event: PointerEvent) => {
            if (pointerIdRef.current !== null && event.pointerId !== pointerIdRef.current) {
                return;
            }

            if (marqueeActiveRef.current) {
                const ctrlPressed = event.ctrlKey;

                const previousSelection = selectedAtDragStartRef.current;

                const nextSelected = ctrlPressed
                    ? mergeUniqueById(previousSelection, preselectedRef.current)
                    : preselectedRef.current;

                dispatch(ItemsControlActions.updateSelectedItems(nextSelected));
            }

            window.removeEventListener("pointermove", handlePointerMove);
            window.removeEventListener("pointerup", handlePointerUp);
            window.removeEventListener("pointercancel", handlePointerUp);

            resetMarquee();
        },
        [dispatch, handlePointerMove, resetMarquee],
    );

    const handlePointerDown = useCallback(
        (event: React.PointerEvent<HTMLDivElement>) => {
            if (pointerIdRef.current !== null) {
                return;
            }

            if (event.pointerType === "mouse" && event.button !== 0) {
                return; // Only respond to left mouse button
            }

            const targetElement = event.target as HTMLElement | null;
            if (targetElement?.closest("[data-click-and-drag-ignore]")) {
                return;
            }

            const targetTag = targetElement?.tagName?.toLowerCase();
            if (targetTag && ["input", "textarea", "select", "button", "option"].includes(targetTag)) {
                return;
            }
            const clickedItem = targetElement?.closest("[data-click-and-drag-item-id]");

            if (clickedItem) {
                const clickedId = clickedItem.getAttribute("data-click-and-drag-item-id");
                const registered = clickedId ? registeredItemsRef.current.get(clickedId) : null;
                const isSelectable = registered?.item.selectable !== false;
                const isDraggable = registered?.item.draggable !== false;

                if (isSelectable || isDraggable) {
                    return; // Ignore clicks on interactive items
                }
                // If item is neither draggable nor selectable, let marquee handling continue as if empty space
            }

            selectedAtDragStartRef.current = selectedItems;

            if (!event.ctrlKey) {
                dispatch(ItemsControlActions.clearSelectionState());
            }

            pointerIdRef.current = event.pointerId;

            const rect = zoneRef.current?.getBoundingClientRect() ?? null;
            const start = clampPoint({ x: event.clientX, y: event.clientY }, rect);

            startPointRef.current = start;
            marqueeActiveRef.current = false;
            dispatch(ItemsControlActions.updateMarquee(null));

            window.addEventListener("pointermove", handlePointerMove);
            window.addEventListener("pointerup", handlePointerUp);
            window.addEventListener("pointercancel", handlePointerUp);
        },
        [dispatch, handlePointerMove, handlePointerUp, selectedItems],
    );

    useEffect(() => {
        if (!marquee?.start || !marquee?.current) {
            if (preselectedItems.length) {
                dispatch(ItemsControlActions.updatePreselectedItems([]));
            }
            return;
        }

        const selectionRect = normalizeRect(marquee.start, marquee.current);
        // console.warn('Selection rect:', selectionRect);
        // console.warn('Registered items:', registeredItemsRef.current.size);

        const nextPreselected: IUniversalItem[] = [];
        registeredItemsRef.current.forEach(({ item, ref }) => {
            if (item.selectable === false) {
                return;
            }
            const element = ref.current;
            // console.warn(`Item ${item.id} - element:`, element);
            if (!element) {
                // console.warn(`Item ${item.id} - NO ELEMENT FOUND`);
                return;
            }
            const rect = element.getBoundingClientRect();
            // console.warn(`Item ${item.id} - rect:`, rect);
            const intersects = rectanglesIntersect(selectionRect, rect);
            // console.warn(`Item ${item.id} - intersects:`, intersects);
            if (intersects) {
                nextPreselected.push(item);
            }
        });

        // console.warn('Next preselected:', nextPreselected);

        const prevIds = new Set(preselectedItems.map((item) => item.id));
        const nextIds = new Set(nextPreselected.map((item) => item.id));
        const sameSelection = prevIds.size === nextIds.size && Array.from(nextIds).every((id) => prevIds.has(id));

        if (!sameSelection) {
            dispatch(ItemsControlActions.updatePreselectedItems(nextPreselected));
        }
    }, [dispatch, marquee, preselectedItems, registryVersion]);

    useEffect(() => {
        if (overDropzone && !registeredItemsRef.current.has(overDropzone.id)) {
            dispatch(ItemsControlActions.updateOverDropzone(null));
        }
    }, [dispatch, overDropzone, registryVersion]);

    useEffect(() => {
        return () => {
            window.removeEventListener("pointermove", handlePointerMove);
            window.removeEventListener("pointerup", handlePointerUp);
            window.removeEventListener("pointercancel", handlePointerUp);
            resetMarquee();
            dispatch(ItemsControlActions.updatePreselectedItems([]));
        };
    }, [dispatch, handlePointerMove, handlePointerUp, resetMarquee]);

    const overlayStyle: CSSProperties | null = useMemo(() => {
        if (!marquee || !marquee.current || !marquee.start || !zoneRef.current) {
            return null;
        }

        const rect = zoneRef.current.getBoundingClientRect();
        const normalized = normalizeRect(marquee.start, marquee.current);

        const left = normalized.left - rect.left;
        const top = normalized.top - rect.top;

        return {
            left,
            top,
            width: normalized.width,
            height: normalized.height,
        };
    }, [marquee]);

    const contextValue = useMemo(
        () => ({
            registerItem,
            unregisterItem,
        }),
        [registerItem, unregisterItem],
    );

    return (
        <ClickAndDragZoneContext.Provider value={contextValue}>
            <div id={props.id} ref={zoneRef} className="click-and-drag-zone" onPointerDown={handlePointerDown}>
                {props.children}

                {overlayStyle && <div className="click-and-drag-marquee" style={overlayStyle} />}

                {props.debug && (
                    <div className="click-and-drag-text noselect">
                        <h1>{props.id}</h1>
                        {draggingType === "SELECTION" && "Selecting..."}
                        {draggingType === "MOVE" && !overDropzone && "Moving..."}
                        {draggingType === "MOVE" && overDropzone && "Idling..."}
                        {draggingType === "COPY" && "Copying..."}
                        {preselectedItems.length > 0 && (
                            <>
                                <h1>Preselected:</h1>
                                {preselectedItems.map((item) => (
                                    <p key={item.id}>{item.id}</p>
                                ))}
                            </>
                        )}
                        {selectedItems.length > 0 && (
                            <>
                                <h1>Selected:</h1>
                                {selectedItems.map((item) => (
                                    <p key={item.id}>{item.id}</p>
                                ))}
                            </>
                        )}
                        {draggedItems.length > 0 && (
                            <>
                                <h1>Dragging:</h1>
                                {draggedItems.map((item) => (
                                    <p key={item.id}>{item.id}</p>
                                ))}
                            </>
                        )}
                        {overDropzone && (
                            <>
                                <h1>Over:</h1>
                                <p>{overDropzone.id}</p>
                            </>
                        )}
                    </div>
                )}
            </div>
        </ClickAndDragZoneContext.Provider>
    );
}
